import { useQuery } from '@tanstack/react-query';
import type { Translated } from '@uefadigital/panenka';
import { PkPopover } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useState, useMemo } from 'react';

import { useTicketingOptions } from '../../store';

import Dialog from './Dialog';

import { useWindowSize } from '@hooks/useWindowSize';
import { getRoundParticipantsQuery } from '@queries/competition';
import { hydrateWithTranslations } from '@services/TranslatorService';
import { withErrorBoundary } from '@shared/ErrorBoundary';
import TeamIdentifier from '@shared/Identifier/TeamIdentifier';
import TeamBadge from '@shared/TeamBadge';
import { useTranslateFn } from '@shared/Translate';
import type { Team } from '@shared-types/match.types';
import { RoundPhase, TeamType } from '@shared-types/match.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { orderBy } from '@utilities/collection-utils';

import * as styles from './styles.module.scss';

const fakeTeam = {
  id: '',
  typeTeam: TeamType.PLACEHOLDER,
} as const;

const YourTeam = () => {
  const {
    options: { competitionId, seasonYear },
  } = useTicketingOptions();
  const [selectedTeam, setSelectedTeam] = useState<Translated<Team>>(null);
  const t = useTranslateFn();
  const { isDesktop } = useWindowSize();

  const handleSelectTeam = (t: Translated<Team>) => () => {
    setSelectedTeam(t);
  };

  const handleClose = () => {
    setSelectedTeam(null);
  };

  const handlePopoverOpen = () => {
    GoogleTagManagerHelper.event('Click', 'Select placeholder', 'ticketing');
  };
  const { data: teamsGroup } = useQuery({
    ...getRoundParticipantsQuery({
      competitionId,
      seasonYear,
      phase: RoundPhase.TOURNAMENT,
    }),
    select: (data) => orderBy(data, (rg) => rg.round.orderInCompetition)?.at(0),
  });

  const qualifiedTeams = useMemo(
    () =>
      teamsGroup?.teams
        ?.filter((t) => !t.isPlaceHolder)
        .map((t) => hydrateWithTranslations(t))
        .toSorted((prev, next) => prev.displayName.localeCompare(next.displayName)),
    [teamsGroup],
  );
  const notQualifiedTeamsCount = useMemo(
    () => teamsGroup?.round?.teamCount - qualifiedTeams?.length,
    [qualifiedTeams?.length, teamsGroup?.round?.teamCount],
  );

  if (!qualifiedTeams?.length) return;

  return (
    <div
      className={clsx('pk-container', 'pk-py--l pk-py-md--xl2', 'pk-overflow--hidden', 'pk-bg--background')}
      pk-theme='light'
    >
      <h2 className='pk-col pk-col--span-12 pk-text--text-01 pk-font--comp-bold pk-font-size--xl pk-font-size-md--xl2'>
        {t('ticketing_pna_title')}
      </h2>
      <h3 className='pk-col pk-col--span-12 pk-text--text-01 pk-font--base-semibold pk-font-size--l'>
        {t('ticketing_pna_subtitle')}
      </h3>
      {!!qualifiedTeams?.length && (
        <div className={clsx('pk-col pk-col--span-12', styles.teamsWrapper)}>
          {qualifiedTeams.map((team) => (
            <button type='button' key={`teamwrapper-${team.id}`} onClick={handleSelectTeam(team)}>
              <TeamIdentifier
                className={clsx(styles.identifier, 'pk-py--0')}
                team={team}
                type='vertical'
                badgeSize={isDesktop ? 80 : 48}
                showTeamCountry={false}
              />
            </button>
          ))}
          {!!notQualifiedTeamsCount && (
            <PkPopover className={styles.popover} placement='bottom' onPkShow={handlePopoverOpen}>
              <div
                slot='trigger'
                className={clsx(
                  'pk-position--relative',
                  'pk-d--flex',
                  'pk-justify-content--center',
                  styles.notQualified,
                )}
              >
                <TeamBadge className={styles.identifier} team={fakeTeam} size={isDesktop ? 80 : 48} />
                <div
                  className={clsx(
                    'pk-position--absolute',
                    'pk-font--base-semibold',
                    'pk-text--text-01',
                    'pk-font-size--l',
                    'pk-font-size-md--xl',
                    'pk-w--100',
                    'pk-d--flex',
                    'pk-justify-content--center',
                    'pk-align-items--center',
                    'pk-top--0',
                  )}
                  style={{
                    height: isDesktop ? 80 : 48,
                  }}
                >{`+${teamsGroup.round.teamCount - qualifiedTeams.length}`}</div>
              </div>
              <div slot='header'>{t('ticketing_pna_tootip_title')}</div>
              <div>{t('ticketing_pna_tootip_text')}</div>
            </PkPopover>
          )}
        </div>
      )}
      <Dialog selectedTeam={selectedTeam} handleClose={handleClose} />
    </div>
  );
};

export default withErrorBoundary(memo(YourTeam));
